import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';

const POSITION_STALE_TIME_MS = 3_000;
const POSITION_REFETCH_INTERVAL_MS = 5_000;

export interface LivePosition {
    coin: string | null;
    /** Absolute position size in asset units. */
    size: number | null;
    isLong: boolean | null;
    entryPx: number | null;
    /** Authoritative liquidation price from Hyperliquid. */
    liquidationPx: number | null;
    /** Backend-computed unrealized PnL in USD. */
    unrealizedPnl: number | null;
    /** Return on equity as a fraction (0.25 = +25%). */
    returnOnEquity: number | null;
    /** Margin currently locked into the position (USD). */
    marginUsed: number | null;
    /** Notional position value (USD). */
    positionValue: number | null;
    /** Effective leverage of the live position. */
    leverage: number | null;
    /** Margin mode Hyperliquid reports for the position; only isolated positions accept a margin top-up. */
    marginType: 'isolated' | 'cross' | null;
    hasPosition: boolean;
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
}

function num(raw: string | null | undefined): number | null {
    if (raw === null || raw === undefined) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

const EMPTY: Omit<
    LivePosition,
    'isLoading' | 'isError' | 'refetch' | 'hasPosition' | 'coin'
> = {
    size: null,
    isLong: null,
    entryPx: null,
    liquidationPx: null,
    unrealizedPnl: null,
    returnOnEquity: null,
    marginUsed: null,
    positionValue: null,
    leverage: null,
    marginType: null,
};

/**
 * Reads a position for one coin from `GET /speed/position/:coin`. The backend
 * (Hyperliquid) is the source of truth for liquidation price, unrealized PnL,
 * margin and effective leverage — this is what the position panel and chart
 * show instead of an optimistic client-side reconstruction.
 *
 * Callers pass the coin, so the share card can read a position for a market
 * that isn't the selected one. The query key is per-coin, so asking for the
 * coin the trading UI is already polling is a cache hit, not a second request.
 * `poll` is off for one-shot readers.
 */
export function usePositionForCoin(
    coin: string | null,
    enabled: boolean,
    poll = false,
): LivePosition {
    // position read moved to factions.backend's speed facet (V1).
    const backend = useSpeedBackend();
    const isEnabled = enabled && !!backend && !!coin;

    const query = useQuery({
        queryKey: queryKeys.speed.position(coin ?? ''),
        queryFn: async ({ signal }) => backend!.fetchPosition(coin!, signal),
        enabled: isEnabled,
        staleTime: POSITION_STALE_TIME_MS,
        refetchInterval: poll ? POSITION_REFETCH_INTERVAL_MS : false,
    });

    const derived = useMemo(() => {
        const position = query.data?.position ?? null;
        if (!position) {
            return { ...EMPTY, hasPosition: false };
        }
        const szi = num(position.szi);
        return {
            size: szi !== null ? Math.abs(szi) : null,
            isLong: szi !== null ? szi >= 0 : null,
            entryPx: num(position.entryPx),
            liquidationPx: num(position.liquidationPx),
            unrealizedPnl: num(position.unrealizedPnl),
            returnOnEquity: num(position.returnOnEquity),
            marginUsed: num(position.marginUsed),
            positionValue: num(position.positionValue),
            leverage: position.leverage?.value ?? null,
            marginType: position.leverage?.type ?? null,
            hasPosition: true,
        };
    }, [query.data]);

    return {
        coin,
        ...derived,
        isLoading: isEnabled && query.isLoading,
        isError: query.isError,
        refetch: () => void query.refetch(),
    };
}

/**
 * The trading UI's own position: the coin comes from the recovered/active
 * trade, falling back to the selected market, and it polls while a position is
 * open/pending.
 */
export function useLivePosition(): LivePosition {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const executionStatus = useTradingStore((s) => s.executionStatus);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);

    // The CHARTED market, full stop. This used to prefer a `recoveredCoin`
    // global — "the coin of the one position" — which meant that after opening
    // BTC the poll kept fetching BTC no matter where the user navigated, and
    // its result was then filed under whatever market they had moved to.
    const coin = selectedCoin ?? null;
    const isActive =
        executionStatus === 'open' ||
        executionStatus === 'pending' ||
        executionStatus === 'closing';

    return usePositionForCoin(coin, isRealMode && isActive, true);
}
